import { Component, StrictMode, type ErrorInfo, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConvexAuthProvider } from '@convex-dev/auth/react';

import { convex } from './lib/convex';

import './styles/tokens.css';
import './styles/base.css';

import { LandingPage } from './routes/Landing';
import { SignInPage } from './routes/SignIn';
import { SignUpPage } from './routes/SignUp';
import { ForgotPasswordPage } from './routes/ForgotPassword';
import { ResetPasswordPage } from './routes/ResetPassword';
import { AppleCallback } from './routes/AppleCallback';
import { GoogleCallback } from './routes/GoogleCallback';
import { HomePage } from './routes/Home';
import { BuilderPage } from './routes/Builder';
import { PublicProfilePage } from './routes/PublicProfile';
import { NotificationsPage } from './routes/Notifications';
import { SettingsPage } from './routes/Settings';
import { AIPage } from './routes/AI';
import { AIReviewPage } from './routes/AIReview';
import { AILatestRedirect } from './routes/AILatestRedirect';
import { AccountDeletePage } from './routes/AccountDelete';
import { PrivacyPage } from './routes/Privacy';
import { TermsPage } from './routes/Terms';
import { GetPage } from './routes/Get';
import { AccountRestorePage } from './routes/AccountRestore';
import { DMPage } from './routes/DM';
import { DMThreadPage } from './routes/DMThread';
import { RatingsPage } from './routes/Ratings';
import { NotFoundPage } from './routes/NotFound';
import { AppShell, PublicShell } from './components';

// Plain React error boundary. Logs to console; in Phase 1 we don't ship
// browser logs server-side. Replace with a richer telemetry path if/when
// we re-add a dedicated error-tracking surface (see plan §13.12).
// ErrorBoundary fallback — when something throws inside React's tree.
// Renders a banner-shaped error card (eyebrow + headline + body + two
// CTAs) styled to match the rest of the app rather than a bare
// "Something went wrong" line. The full error lands in the browser
// console (`ErrorBoundary caught`) so devtools surfaces the detail
// even though we hide it from the user.
//
// Reload uses location.reload() instead of router.navigate so a stuck
// store / hook-order glitch gets a clean React root.
class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <main
          style={{
            minHeight: '100vh',
            background: 'var(--bg, #FAF6F0)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
          }}
        >
          <div
            role="alert"
            style={{
              width: '100%',
              maxWidth: 460,
              background: 'var(--surface, #fff)',
              borderRadius: 22,
              padding: '32px 28px',
              boxShadow: '0 24px 64px rgba(0,0,0,0.12)',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 11,
                letterSpacing: 2,
                color: 'var(--coral, #E89A6E)',
                textTransform: 'uppercase',
                marginBottom: 10,
              }}
            >
              Something hiccupped
            </div>
            <h1
              style={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontStyle: 'italic',
                fontSize: 28,
                fontWeight: 500,
                margin: '0 0 12px',
                color: 'var(--ink, #2B1D14)',
                lineHeight: 1.2,
              }}
            >
              That didn&rsquo;t go to plan.
            </h1>
            <p
              style={{
                fontSize: 14,
                lineHeight: 1.55,
                color: 'var(--ink-2, #5E4F45)',
                margin: '0 0 22px',
              }}
            >
              We hit a snag loading this page. A refresh usually does it. If it keeps happening, head back home and try again from there.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                type="button"
                onClick={() => location.reload()}
                style={{
                  background: 'var(--coral, #E89A6E)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 100,
                  padding: '11px 22px',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Refresh the page
              </button>
              <button
                type="button"
                onClick={() => { location.href = '/home'; }}
                style={{
                  background: 'transparent',
                  color: 'var(--ink-3, #8A7765)',
                  border: '1px solid var(--line, rgba(43,29,20,0.12))',
                  borderRadius: 100,
                  padding: '10px 22px',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Back home
              </button>
            </div>
          </div>
        </main>
      );
    }
    return this.props.children;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000 },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      {/*
        ConvexAuthProvider — wraps the app so any descendant can call
        Convex Auth's react hooks (useAuthActions, useAuthToken, etc.)
        and so Convex queries auto-attach the active session's JWT.

        PHASE 1 STATUS: scaffold only. lib/featureFlags.ts'
        useConvexAuth() returns false in every code path, so lib/api.ts
        keeps routing every auth call to the Go backend. The provider
        is inert until Phase 2 flips the flag — but having it mounted
        now means flipping it later doesn't require touching this
        file. See lib/convex.ts for why.
      */}
      <ConvexAuthProvider client={convex}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            {/* PUBLIC SHELL — marketing landing, auth, public profile,
                legal, public account-delete instructions. Sticky blurred
                top nav + persistent footer with Terms/Privacy/Contact. */}
            <Route element={<PublicShell />}>
              <Route path="/" element={<LandingPage />} />
              <Route path="/auth/signin" element={<SignInPage />} />
              <Route path="/auth/signup" element={<SignUpPage />} />
              <Route path="/auth/forgot" element={<ForgotPasswordPage />} />
              <Route path="/auth/reset" element={<ResetPasswordPage />} />
              <Route path="/account/delete" element={<AccountDeletePage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/p/:slug" element={<PublicProfilePage />} />
              {/* /get — smart-redirect to the right store. Used as
                  the QR-code payload in PaywallSheet on desktop. */}
              <Route path="/get" element={<GetPage />} />
            </Route>

            {/* BARE — onboarding + transient redirect screens. No shell
                because the design treats these as full-screen flows
                without nav distractions. */}
            <Route path="/builder" element={<BuilderPage />} />
            <Route path="/auth/oauth/apple/callback" element={<AppleCallback />} />
            <Route path="/auth/oauth/google/callback" element={<GoogleCallback />} />
            <Route path="/account/restore" element={<AccountRestorePage />} />

            {/* APP SHELL — every signed-in surface. Sidebar with brand,
                nav, Share CTA, Premium pill, user pill. The shell does
                its own auth gate + profile-complete redirect so child
                pages can assume both. */}
            <Route element={<AppShell />}>
              <Route path="/home" element={<HomePage />} />
              <Route path="/ratings" element={<RatingsPage />} />
              {/* /profile dropped — profile content lives on /home now.
                  Bookmarks to /profile fall through to the catch-all
                  (currently none); add a <Navigate to="/home" /> here
                  if old links surface in analytics. */}
              <Route path="/notifications" element={<NotificationsPage />} />
              {/* Back-compat for any cached push payloads or browser
                  bookmarks left over from when this page was /inbox. */}
              <Route path="/inbox" element={<Navigate to="/notifications" replace />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/ai" element={<AIPage />} />
              {/* Resolver route — notifications deep-link here so they
                  always land on the currently-surviving review for the
                  personality (older reviews get pruned at completion). */}
              <Route path="/ai/latest/:personality" element={<AILatestRedirect />} />
              <Route path="/ai/:id" element={<AIReviewPage />} />
              <Route path="/dm" element={<DMPage />} />
              <Route path="/dm/:threadId" element={<DMThreadPage />} />
            </Route>

            {/* Catch-all 404 — rendered inside PublicShell so the
                wordmark nav + footer remain available to the user.
                Must come last so it only matches unmatched paths. */}
            <Route element={<PublicShell />}>
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
      </ConvexAuthProvider>
    </ErrorBoundary>
  </StrictMode>
);
